/**
 * HuddleBanner — AI-generated practice huddle brief.
 *
 * - Intelligence layer generates items via Claude tool_use (cached 15 min).
 * - Hover-revealed Sparkles icon per item dispatches nexonurse:open-chatbot
 *   to open agent chat with pre-built context (explain action).
 * - "Share as Agenda" opens the browser email client via mailto:.
 * - Collapsible; collapse state persists to sessionStorage.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronUp, RefreshCw, Send, AlertCircle, AlertTriangle, CheckCircle2, Info, Sparkles, Loader2 } from 'lucide-react';
import { fetchHuddleBrief } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useConsumerUnit } from '../../contexts/ConsumerUnitContext';

// Icon component per signal type
const ICON_MAP = {
  critical: <AlertCircle size={15} color="var(--red-critical)" style={{ flexShrink: 0, marginTop: 2 }} />,
  watch:    <AlertTriangle size={15} color="var(--yellow)" style={{ flexShrink: 0, marginTop: 2 }} />,
  ok:       <CheckCircle2 size={15} color="var(--green)" style={{ flexShrink: 0, marginTop: 2 }} />,
  info:     <Info size={15} color="var(--aqua)" style={{ flexShrink: 0, marginTop: 2 }} />,
};

/** Render text with **bold** markdown as <strong> nodes. */
function renderMarkdown(text) {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1
      ? <strong key={i} style={{ fontWeight: 600, color: 'var(--navy-deep)' }}>{part}</strong>
      : part
  );
}

export default function HuddleBanner() {
  const { getToken } = useAuth();
  const { selectedUnitId } = useConsumerUnit();
  const [isOpen, setIsOpen] = useState(() => {
    try { return sessionStorage.getItem('nn_huddle_open') !== 'false'; } catch { return true; }
  });
  const [narrative, setNarrative] = useState('');
  const [items, setItems] = useState([]);
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [regenerating, setRegenerating] = useState(false);

  // Load brief from API
  const loadBrief = useCallback(async (opts = {}) => {
    try {
      const data = await fetchHuddleBrief(selectedUnitId, getToken);
      if (data?.items && Array.isArray(data.items) && data.items.length > 0) {
        setItems(data.items);
        setNarrative(data.narrative || '');
      } else if (Array.isArray(data) && data.length > 0) {
        // Backward compat: old shape returned bare array
        setItems(data);
        setNarrative('');
      }
    } catch { /* keep current items */ }
  }, [getToken, selectedUnitId]);

  // Fetch on mount and when selectedUnitId changes
  useEffect(() => {
    let cancelled = false;
    loadBrief().then(() => { if (cancelled) return; });
    return () => { cancelled = true; };
  }, [loadBrief]);

  const toggle = () => {
    const next = !isOpen;
    setIsOpen(next);
    try { sessionStorage.setItem('nn_huddle_open', String(next)); } catch {}
  };

  const regenerate = async (e) => {
    e.stopPropagation();
    if (regenerating) return;
    setRegenerating(true);
    try {
      await loadBrief({ regenerate: true });
    } finally {
      setRegenerating(false);
    }
  };

  const handleExplain = (item) => {
    window.dispatchEvent(new CustomEvent('nexonurse:open-chatbot', {
      detail: {
        agent: true,
        alertId: item.alertId || undefined,
        domain: item.domain,
        firstMessage: item.explainPrompt,
      },
    }));
  };

  const shareAsAgenda = () => {
    const body = items
      .map((it, i) => `${i + 1}. ${it.text.replace(/\*\*/g, '')}`)
      .join('\n');
    const subject = encodeURIComponent("Today's Practice Huddle Agenda");
    window.location.href = `mailto:?subject=${subject}&body=${encodeURIComponent(`Today's Huddle Agenda:\n\n${body}\n\n— Shared from NexoNurse Practice Pulse`)}`;
  };

  return (
    <div
      style={{
        background: 'var(--white)',
        borderRadius: 12,
        boxShadow: 'var(--shadow-card)',
        overflow: 'hidden',
        animation: 'fadeUp 0.3s ease both',
      }}
    >
      {/* Header row — entire div is clickable for expand/collapse */}
      <div
        onClick={toggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
        aria-expanded={isOpen}
        aria-label={isOpen ? 'Collapse huddle brief' : 'Expand huddle brief'}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '13px 18px',
          borderBottom: isOpen ? '1px solid var(--surface)' : 'none',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy-deep)' }}>
          Huddle Brief
        </span>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {isOpen && (
            <button
              type="button"
              onClick={regenerate}
              title="Regenerate brief"
              aria-label="Regenerate huddle brief"
              disabled={regenerating}
              style={{
                background: 'none', border: 'none', cursor: regenerating ? 'wait' : 'pointer',
                color: 'var(--text-dark-secondary)', padding: '5px 6px',
                borderRadius: 6, display: 'flex', alignItems: 'center',
                opacity: regenerating ? 0.5 : 1,
              }}
            >
              {regenerating
                ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                : <RefreshCw size={14} />}
            </button>
          )}
          <div
            style={{
              color: 'var(--text-dark-secondary)', padding: '5px 6px',
              borderRadius: 6, display: 'flex', alignItems: 'center',
            }}
          >
            {isOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
          </div>
        </div>
      </div>

      {/* Item list */}
      {isOpen && (
        <>
          <div style={{ padding: '12px 18px 8px', display: 'flex', flexDirection: 'column', gap: 7 }}>
            {/* Narrative summary */}
            {narrative && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9, marginBottom: 4 }}>
                <Sparkles size={15} color="var(--aqua)" style={{ flexShrink: 0, marginTop: 2 }} />
                <span style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6, fontStyle: 'italic' }}>
                  {narrative}
                </span>
              </div>
            )}

            {/* Huddle items */}
            {items.map((item, i) => (
              <div
                key={i}
                style={{ display: 'flex', alignItems: 'flex-start', gap: 9, position: 'relative' }}
                onMouseEnter={() => setHoveredIdx(i)}
                onMouseLeave={() => setHoveredIdx(null)}
              >
                {ICON_MAP[item.type] || ICON_MAP.info}
                <span style={{ fontSize: 13, color: 'var(--text-dark)', lineHeight: 1.6, flex: 1, minWidth: 0 }}>
                  {renderMarkdown(item.text)}
                </span>
                {item.explainPrompt && (
                  <button
                    type="button"
                    onClick={() => handleExplain(item)}
                    title="Explain this item"
                    aria-label={`Explain: ${item.text.replace(/\*\*/g, '').slice(0, 40)}`}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      padding: '2px 4px', display: 'flex', alignItems: 'center',
                      opacity: hoveredIdx === i ? 1 : 0,
                      transition: 'opacity 0.15s ease',
                      color: 'var(--aqua)',
                      flexShrink: 0, marginTop: 2,
                    }}
                  >
                    <Sparkles size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Footer */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 18px 13px',
            }}
          >
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>
              AI-generated from live domain signals. Review before sharing.
            </span>
            <button
              type="button"
              onClick={shareAsAgenda}
              style={{
                fontSize: 11.5, fontWeight: 600, color: 'var(--aqua)',
                background: 'none', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 4, padding: 0,
              }}
            >
              <Send size={11} />
              Share as Agenda
            </button>
          </div>
        </>
      )}
    </div>
  );
}
